"use client";

import Link from "next/link";
import { ArrowRight, Plug } from "lucide-react";
import {
  CrossSellBand,
  DevLandingShell,
  FinalCta,
  GetKeyButton,
  HeroBadge,
  SecondaryCta,
  SectionHeading,
  StatsRow,
  metalText,
} from "@/components/marketing/dev-landing/chrome";
import { TextFeatureList } from "@/components/marketing/dev-landing/sections";
import { CodeWindow, K, Line, P, S } from "@/components/marketing/dev-landing/code-window";
import { ChatDemo, Hi } from "@/components/marketing/dev-landing/chat-demo";
import {
  ClaudeConnectSteps,
  McpVideoPlaceholder,
  WorksWithRow,
} from "@/components/marketing/dev-landing/mcp-connect";
import { ToolCatalog } from "@/components/marketing/dev-landing/tool-catalog";

const q = (v: string) => `"${v}"`;

export default function McpLandingClient({ keyHref }: { keyHref: string }) {
  return (
    <DevLandingShell>
      {/* Hero */}
      <div className="text-center">
        <HeroBadge>
          <span className="text-sm" style={metalText}>
            &#10022;
          </span>
          <span className="text-sm text-[#d4d5d5]">MCP server &middot; live at /api/mcp</span>
        </HeroBadge>

        <h1 className="mx-auto max-w-4xl text-balance text-4xl tracking-tight text-white sm:text-5xl md:text-6xl">
          Give your AI <span style={metalText}>real-time options intelligence</span>
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-balance text-lg leading-relaxed text-white/70 sm:text-xl">
          17 tools - gamma exposure, options flows, screeners and your own wheel analytics - served
          over MCP to Claude, ChatGPT, Cursor or any agent you build.
        </p>

        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <GetKeyButton keyHref={keyHref} eventName="mcp_lp_get_key_clicked" />
          <SecondaryCta href="#connect">
            <Plug className="h-4 w-4" />
            How to connect
          </SecondaryCta>
        </div>
        <p className="mt-4 text-xs text-white/50 sm:text-sm">Included with every paid QuantWheel plan.</p>

        <WorksWithRow />
      </div>

      {/* Demo video */}
      <McpVideoPlaceholder
        youtubeEmbedUrl="https://www.youtube-nocookie.com/embed/WHBlx9aTTXM?start=245"
        label="Watch Claude pull live GEX walls, scan options flow and review a wheel portfolio through the QuantWheel MCP connector."
      />

      <StatsRow
        stats={[
          { value: "17", label: "MCP tools" },
          { value: "Live", label: "market data" },
          { value: "<1min", label: "Claude setup" },
          { value: "0", label: "lines of code" },
        ]}
      />

      {/* Chat demo */}
      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title={
            <>
              Ask in English, <span style={metalText}>answer from the tape</span>
            </>
          }
          sub="Once connected, your AI calls QuantWheel tools on its own - pulling live numbers instead of guessing from stale training data."
        />
        <ChatDemo
          turns={[
            {
              question: "Where are the gamma walls on SPY today, and which side is dealer hedging leaning?",
              toolCalls: [{ tool: "get_gex", args: "SPY" }],
              answer: (
                <>
                  SPY is in <Hi>positive gamma</Hi> with the flip at <Hi>620.5</Hi>. The call wall sits at{" "}
                  <Hi>630</Hi> and the put wall at <Hi>615</Hi>, dealer hedging dampens moves and often pinns around the walls. With negative vanna, put wall is going to be strong today. 
                </>
              ),
            },
            {
              question: "Scan my watchlist for puts worth selling into that setup.",
              toolCalls: [
                { tool: "find_options_to_sell", args: "SPY, NVDA, AMD" },
                { tool: "get_flow_bars", args: "NVDA" },
              ],
              answer: (
                <>
                  Best fit: <Hi>NVDA 08/15 175P</Hi> at ~<Hi>1.9% premium yield</Hi> with positive gamma and negative vanna, put wall where dealer
                  hedging tends to slow declines.
                </>
              ),
            },
          ]}
        />
      </div>

      {/* Connect to Claude */}
      <ClaudeConnectSteps toolsHint="all 17 tools - GEX, flows, screeners and your wheel analytics" />

      {/* Cursor / custom agent config */}
      <div className="mx-auto mt-10 max-w-3xl">
        <p className="mb-3 text-center text-sm text-white/50">
          Wiring up Cursor or a custom agent instead? One block of config with your API key:
        </p>
        <CodeWindow title=".cursor/mcp.json" status="any MCP client">
          <Line>
            <P>{"{"}</P>
          </Line>
          <Line indent={1}>
            <K>{q("mcpServers")}</K>
            <P>: {"{"}</P>
          </Line>
          <Line indent={2}>
            <K>{q("quantwheel")}</K>
            <P>: {"{"}</P>
          </Line>
          <Line indent={3}>
            <K>{q("url")}</K>
            <P>: </P>
            <S>{q("https://quantwheel.com/api/mcp")}</S>
            <P>,</P>
          </Line>
          <Line indent={3}>
            <K>{q("headers")}</K>
            <P>: {"{ "}</P>
            <K>{q("Authorization")}</K>
            <P>: </P>
            <S>{q("Bearer qw_live_...")}</S>
            <P>{" }"}</P>
          </Line>
          <Line indent={2}>
            <P>{"}"}</P>
          </Line>
          <Line indent={1}>
            <P>{"}"}</P>
          </Line>
          <Line>
            <P>{"}"}</P>
          </Line>
        </CodeWindow>
      </div>

      {/* Tool catalog */}
      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title="17 tools, four surfaces"
          sub="Everything below becomes callable the moment you connect - no schemas to write, no glue code."
        />
        <ToolCatalog
          groups={[
            {
              title: "GEX & dealer positioning",
              description: "Real-time gamma exposure for any US optionable ticker.",
              tools: [
                { name: "get_gex", blurb: "walls, flip and per-strike GEX" },
                { name: "get_gex_heatmap", blurb: "GEX by strike and expiration" },
                { name: "get_gamma_flip_events", blurb: "flip & wall-move events" },
                { name: "get_vanna_charm", blurb: "second-order Greeks exposure" },
                { name: "get_max_pain", blurb: "max pain per expiration" },
                { name: "screen_gex_tickers", blurb: "market-wide GEX setups" },
              ],
            },
            {
              title: "Options flows",
              description: "Premium drift and significant prints, straight off the tape.",
              tools: [
                { name: "get_flow_bars", blurb: "net call/put premium bars", tag: "Quant" },
                { name: "get_flow_trades", blurb: "sweeps, blocks and splits", tag: "Quant" },
              ],
            },
            {
              title: "Market & screening",
              description: "Quotes, chains and options-selling candidates on demand.",
              tools: [
                { name: "get_stock_quote", blurb: "live quote for any symbol" },
                { name: "get_option_expirations", blurb: "available expirations" },
                { name: "find_options_to_sell", blurb: "premium-selling screener" },
              ],
            },
            {
              title: "Your trading",
              description: "Your journal and wheel data, readable by your AI.",
              tools: [
                { name: "get_active_wheels", blurb: "open wheel positions" },
                { name: "get_journal_performance", blurb: "P&L over time" },
                { name: "get_all_time_performance", blurb: "lifetime stats" },
                { name: "get_suggested_actions", blurb: "next-step suggestions" },
              ],
            },
          ]}
        />
      </div>

      {/* Why it's different */}
      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title={
            <>
              Why traders wire this in <span style={metalText}>first</span>
            </>
          }
        />
        <TextFeatureList
          items={[
            {
              title: "Live numbers, not vibes",
              description:
                "LLMs guess; tools measure. Every answer is grounded in the same real-time GEX and flow engine that powers QuantWheel's dashboards.",
            },
            {
              title: "Your portfolio in the loop",
              description:
                "Because your wheels and journal are tools too, the AI can weigh a new trade against what you already hold - not in the abstract.",
            },
            {
              title: "OAuth 2.1, revocable anytime",
              description:
                "Claude connects through OAuth scoped to your account - no keys pasted into chat. Custom agents can use a Bearer key you rotate yourself.",
            },
            {
              title: "One URL, every client",
              description:
                "Streamable HTTP MCP works in Claude, ChatGPT, Cursor, Claude Code and anything built on the MCP SDK - same URL everywhere.",
            },
          ]}
        />
      </div>

      {/* Go deeper */}
      <div className="mt-20 grid gap-5 sm:mt-24 md:grid-cols-2">
        {[
          {
            href: "/gex-mcp",
            label: "GEX MCP",
            title: "Deep dive: gamma exposure tools",
            description: "Walls, flips, heatmaps and GEX screening - the dedicated tour for dealer-positioning workflows.",
          },
          {
            href: "/options-flows-mcp",
            label: "Options Flows MCP",
            title: "Deep dive: options flow tools",
            description: "Premium drift and significant prints in conversation - the dedicated tour for flow traders.",
          },
        ].map((card) => (
          <Link
            key={card.href}
            href={card.href}
            className="group rounded-xl border border-[#1f2020] bg-[#101111] p-6 transition-colors hover:border-[#3a3c3b]"
          >
            <span className="text-xs uppercase tracking-wider text-[#8d918f]">{card.label}</span>
            <h3 className="mt-2 flex items-center gap-2 text-lg tracking-tight text-white">
              {card.title}
              <ArrowRight className="h-4 w-4 text-[#9aa09e] transition-transform group-hover:translate-x-0.5" />
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-white/60">{card.description}</p>
          </Link>
        ))}
      </div>

      <CrossSellBand
        badge="Building software instead?"
        title="The same engine, as a REST API"
        description="Everything the MCP tools can do is also a clean JSON endpoint - GEX, flows, screeners and analytics over plain HTTPS."
        href="/api"
        linkLabel="Explore the API"
      />

      <FinalCta
        title="Your AI is one connector away from the options market"
        sub="Add the QuantWheel MCP server to Claude in under a minute, or drop your key into any MCP client."
        keyHref={keyHref}
        eventName="mcp_lp_get_key_clicked"
        note="Included with every paid QuantWheel plan. Flow tools require Quant."
      />
    </DevLandingShell>
  );
}
